import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { CalculatorState, Calculation, Variable } from '../types';

export interface CalculatorData extends CalculatorState {
  sessionId: string;
  lastUpdatedBy: string;
  lastUpdatedAt: Timestamp;
  isLocked: boolean;
  lockedBy?: string;
  lockedAt?: Timestamp;
  calculations?: CalculationData[];
  variables?: VariableData[];
}

export interface CalculationData extends Calculation {
  createdAt: Timestamp;
  sessionId: string;
}

export interface VariableData extends Variable {
  createdAt: Timestamp;
  sessionId: string;
}

class CalculatorService {
  private activeCalculatorListeners: Map<string, () => void> = new Map(); // Unsubscribe functions

  // Update calculator state
  async updateCalculatorState(
    sessionId: string,
    userId: string,
    updates: Partial<CalculatorState>
  ): Promise<void> {
    try {
      const calculatorRef = doc(db, 'sessions', sessionId, 'calculator', 'state');
      
      const updateData = {
        ...updates,
        sessionId,
        lastUpdatedBy: userId,
        lastUpdatedAt: serverTimestamp()
      };
      
      await updateDoc(calculatorRef, updateData);
    } catch (error) {
      throw new Error(`Failed to update calculator state: ${error}`);
    }
  }

  // Get calculator state
  async getCalculatorState(sessionId: string): Promise<CalculatorData | null> {
    try {
      const calculatorRef = doc(db, 'sessions', sessionId, 'calculator', 'state');
      const calculatorDoc = await getDoc(calculatorRef);
      
      if (!calculatorDoc.exists()) {
        return null;
      }
      
      return calculatorDoc.data() as CalculatorData;
    } catch (error) {
      throw new Error(`Failed to get calculator state: ${error}`);
    }
  }

  // Initialize calculator state for a session
  async initializeCalculatorState(sessionId: string, userId: string): Promise<void> {
    try {
      const calculatorRef = doc(db, 'sessions', sessionId, 'calculator', 'state');
      
      const initialState = {
        currentExpression: '',
        currentResult: '',
        isError: false,
        errorMessage: '',
        mode: 'basic',
        isLocked: false,
        sessionId,
        lastUpdatedBy: userId,
        lastUpdatedAt: serverTimestamp()
      };
      
      await updateDoc(calculatorRef, initialState);
    } catch (error) {
      throw new Error(`Failed to initialize calculator state: ${error}`);
    }
  }

  // Lock calculator
  async lockCalculator(sessionId: string, userId: string): Promise<void> {
    try {
      const calculatorRef = doc(db, 'sessions', sessionId, 'calculator', 'state');
      await updateDoc(calculatorRef, {
        isLocked: true,
        lockedBy: userId,
        lockedAt: serverTimestamp(),
        lastUpdatedBy: userId,
        lastUpdatedAt: serverTimestamp()
      });
    } catch (error) {
      throw new Error(`Failed to lock calculator: ${error}`);
    }
  }

  // Unlock calculator
  async unlockCalculator(sessionId: string, userId: string): Promise<void> {
    try {
      const calculatorRef = doc(db, 'sessions', sessionId, 'calculator', 'state');
      await updateDoc(calculatorRef, {
        isLocked: false,
        lockedBy: null,
        lockedAt: null,
        lastUpdatedBy: userId,
        lastUpdatedAt: serverTimestamp()
      });
    } catch (error) {
      throw new Error(`Failed to unlock calculator: ${error}`);
    }
  }

  // Add calculation to history
  async addCalculation(
    sessionId: string,
    calculation: Calculation
  ): Promise<void> {
    try {
      console.log('🧮 CalculatorService: Adding calculation to Firestore:', calculation);
      const calculationData: Omit<CalculationData, 'id'> = {
        ...calculation,
        sessionId,
        createdAt: serverTimestamp() as Timestamp
      };
      
      const calculationRef = doc(db, 'sessions', sessionId, 'calculations', calculation.id);
      await setDoc(calculationRef, calculationData);
      console.log('✅ CalculatorService: Calculation saved successfully to Firestore');
    } catch (error) {
      console.error('❌ CalculatorService: Failed to save calculation:', error);
      throw new Error(`Failed to add calculation: ${error}`);
    }
  }

  // Get calculation history
  async getCalculationHistory(sessionId: string, limitCount: number = 50): Promise<CalculationData[]> {
    try {
      const calculatorRef = doc(db, 'sessions', sessionId, 'calculator', 'state');
      const calculatorDoc = await getDoc(calculatorRef);
      
      if (!calculatorDoc.exists()) {
        return [];
      }
      
      // For now, we'll store calculations in the calculator state
      // In a production app, you might want a separate calculations collection
      const data = calculatorDoc.data() as CalculatorData;
      return data.calculations || [];
    } catch (error) {
      throw new Error(`Failed to get calculation history: ${error}`);
    }
  }

  // Add variable
  async addVariable(
    sessionId: string,
    variable: Variable
  ): Promise<void> {
    try {
      console.log('📊 CalculatorService: Adding variable to Firestore:', variable);
      const variableData: Omit<VariableData, 'id'> = {
        ...variable,
        sessionId,
        createdAt: serverTimestamp() as Timestamp
      };
      
      const variableRef = doc(db, 'sessions', sessionId, 'variables', variable.id);
      await setDoc(variableRef, variableData);
      console.log('✅ CalculatorService: Variable saved successfully to Firestore');
    } catch (error) {
      console.error('❌ CalculatorService: Failed to save variable:', error);
      throw new Error(`Failed to add variable: ${error}`);
    }
  }

  // Remove variable
  async removeVariable(sessionId: string, variableId: string): Promise<void> {
    try {
      const variableRef = doc(db, 'sessions', sessionId, 'variables', variableId);
      await deleteDoc(variableRef);
    } catch (error) {
      throw new Error(`Failed to remove variable: ${error}`);
    }
  }

  // Get variables
  async getVariables(sessionId: string): Promise<VariableData[]> {
    try {
      const calculatorRef = doc(db, 'sessions', sessionId, 'calculator', 'state');
      const calculatorDoc = await getDoc(calculatorRef);
      
      if (!calculatorDoc.exists()) {
        return [];
      }
      
      const data = calculatorDoc.data() as CalculatorData;
      return data.variables || [];
    } catch (error) {
      throw new Error(`Failed to get variables: ${error}`);
    }
  }

  // Listen to calculator state changes
  onCalculatorStateChange(
    sessionId: string,
    callback: (state: CalculatorData | null) => void
  ): () => void {
    const calculatorRef = doc(db, 'sessions', sessionId, 'calculator', 'state');
    
    const unsubscribe = onSnapshot(calculatorRef, (doc) => {
      if (doc.exists()) {
        callback(doc.data() as CalculatorData);
      } else {
        callback(null);
      }
    });
    
    // Store unsubscribe function
    this.activeCalculatorListeners.set(sessionId, unsubscribe);
    
    return unsubscribe;
  }

  // Update user cursor position
  async updateCursorPosition(
    sessionId: string,
    userId: string,
    position: number
  ): Promise<void> {
    try {
      const cursorRef = doc(db, 'sessions', sessionId, 'cursors', userId);
      await updateDoc(cursorRef, {
        userId,
        position,
        lastUpdatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Failed to update cursor position:', error);
    }
  }

  // Update user typing status
  async updateTypingStatus(
    sessionId: string,
    userId: string,
    isTyping: boolean
  ): Promise<void> {
    try {
      const typingRef = doc(db, 'sessions', sessionId, 'typing', userId);
      await updateDoc(typingRef, {
        userId,
        isTyping,
        lastUpdatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Failed to update typing status:', error);
    }
  }

  // Listen to cursor positions
  onCursorPositionsChange(
    sessionId: string,
    callback: (cursors: { [userId: string]: number }) => void
  ): () => void {
    const cursorsRef = collection(db, 'sessions', sessionId, 'cursors');
    
    const unsubscribe = onSnapshot(cursorsRef, (querySnapshot) => {
      const cursors: { [userId: string]: number } = {};
      
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        cursors[data.userId] = data.position;
      });
      
      callback(cursors);
    });
    
    return unsubscribe;
  }

  // Listen to typing status
  onTypingStatusChange(
    sessionId: string,
    callback: (typingUsers: { [userId: string]: boolean }) => void
  ): () => void {
    const typingRef = collection(db, 'sessions', sessionId, 'typing');
    
    const unsubscribe = onSnapshot(typingRef, (querySnapshot) => {
      const typingUsers: { [userId: string]: boolean } = {};
      
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        typingUsers[data.userId] = data.isTyping;
      });
      
      callback(typingUsers);
    });
    
    return unsubscribe;
  }

  // Clear calculator
  async clearCalculator(sessionId: string, userId: string): Promise<void> {
    try {
      const calculatorRef = doc(db, 'sessions', sessionId, 'calculator', 'state');
      await updateDoc(calculatorRef, {
        currentExpression: '',
        currentResult: '',
        isError: false,
        errorMessage: '',
        lastUpdatedBy: userId,
        lastUpdatedAt: serverTimestamp()
      });
    } catch (error) {
      throw new Error(`Failed to clear calculator: ${error}`);
    }
  }

  // Listen for calculator state changes (alias for onCalculatorStateChange)
  listenForCalculatorState(
    sessionId: string,
    callback: (state: CalculatorData | null) => void
  ): () => void {
    return this.onCalculatorStateChange(sessionId, callback);
  }

  // Listen for calculations
  listenForCalculations(
    sessionId: string,
    callback: (calculations: CalculationData[]) => void
  ): () => void {
    const calculationsRef = collection(db, 'sessions', sessionId, 'calculations');
    const q = query(calculationsRef, orderBy('timestamp', 'desc'));
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const calculations: CalculationData[] = [];
      querySnapshot.forEach((doc) => {
        calculations.push({
          ...doc.data() as CalculationData,
          id: doc.id
        });
      });
      callback(calculations);
    });
    
    return unsubscribe;
  }

  // Listen for variables
  listenForVariables(
    sessionId: string,
    callback: (variables: VariableData[]) => void
  ): () => void {
    const variablesRef = collection(db, 'sessions', sessionId, 'variables');
    const q = query(variablesRef, orderBy('timestamp', 'desc'));
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const variables: VariableData[] = [];
      querySnapshot.forEach((doc) => {
        variables.push({
          ...doc.data() as VariableData,
          id: doc.id
        });
      });
      callback(variables);
    });
    
    return unsubscribe;
  }

  // Clear all calculations for a session
  async clearCalculations(sessionId: string): Promise<void> {
    try {
      const calculationsRef = collection(db, 'sessions', sessionId, 'calculations');
      const querySnapshot = await getDocs(calculationsRef);
      
      const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
    } catch (error) {
      throw new Error(`Failed to clear calculations: ${error}`);
    }
  }

  // Clear all variables for a session
  async clearVariables(sessionId: string): Promise<void> {
    try {
      const variablesRef = collection(db, 'sessions', sessionId, 'variables');
      const querySnapshot = await getDocs(variablesRef);
      
      const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
    } catch (error) {
      throw new Error(`Failed to clear variables: ${error}`);
    }
  }

  // Clean up all listeners
  cleanup(): void {
    this.activeCalculatorListeners.forEach(unsubscribe => unsubscribe());
    this.activeCalculatorListeners.clear();
  }
}

// Export singleton instance
export const calculatorService = new CalculatorService();
export default calculatorService;
